/**
 * SignupClient — the storefront's one way to sign someone up.
 * Loaded once (guarded), used by <signup-modal>, <waitlist-modal> and
 * <waitlist-form>; each picks the calls it needs.
 *
 * Two systems, on purpose:
 *  - Shopify's customer form (postCustomerForm): creates/updates the customer
 *    with marketing consent and tags. Klaviyo's Shopify integration syncs both,
 *    so Shopify stays the record of consent.
 *  - Klaviyo's client API: list membership and properties land right away, so
 *    flows and segments work without waiting for the sync.
 *
 * Shopify's storefront CAPTCHA, and why submits used to redirect:
 *  Shopify protects customer/contact forms by attaching an h-captcha-response
 *  token to the POST. A fetch() built from FormData carries no token, so the
 *  request can't be scored and gets bounced to /challenge. The fix is the
 *  documented one: window.Shopify.captcha.protect(form, cb) stamps the token
 *  into the form first; with it present the invisible score nearly always
 *  passes and the POST resolves in place. An interactive challenge is still
 *  possible (suspicious score, bursts) — postCustomerForm reports it and the
 *  caller falls back to a native submit, which the page handles on return.
 *
 * Klaviyo rules baked in here so callers don't have to remember them:
 *  - subscribing and setting properties cannot share a request:
 *    subscribeToList(), then setProfileProperties()
 *  - back-in-stock rejects a repeat for the same profile + variant; that's
 *    treated as success, since the person is on the list either way
 */
(() => {
  if (window.SignupClient) return;

  const REVISION = '2026-01-15';
  const SIGNUPS_KEY = 'signups'; // localStorage: what this browser has signed up for

  /* ── Klaviyo transport ─────────────────────────────────────────────────── */

  const post = (path, companyId, body, { contentType = 'application/vnd.api+json', okStatuses = [] } = {}) =>
    fetch(`https://a.klaviyo.com/client/${path}?company_id=${encodeURIComponent(companyId)}`, {
      method: 'POST',
      headers: { accept: 'application/vnd.api+json', 'content-type': contentType, revision: REVISION },
      body: JSON.stringify(body),
    }).then((res) => {
      if (!res.ok && !okStatuses.includes(res.status)) {
        throw new Error(`Klaviyo ${path} responded ${res.status}`);
      }
      return res;
    });

  const profile = (email, attributes = {}) => ({
    data: { type: 'profile', attributes: { email, ...attributes } },
  });

  /* ── Shopify helpers ───────────────────────────────────────────────────── */

  /** Shopify bounces the customer form back to the page tagged with one of these. */
  const outcomeFromUrl = (url) => {
    const params = new URL(url, location.origin).searchParams;
    if (params.get('customer_posted') === 'true') return 'new';
    if (params.get('form_type') === 'customer') return 'existing';
    return null;
  };

  /**
   * Wire the form into Shopify's CAPTCHA and resolve when it reports ready.
   * By default Shopify only wires forms the user has interacted with; forms
   * submitted by script need this call. No-op when the merchant has CAPTCHA
   * off (window.Shopify.captcha absent). Bounded: if the widget never answers
   * (blocked by a consent app, say) we carry on rather than hang.
   */
  const protect = (form) =>
    new Promise((resolve) => {
      const captcha = window.Shopify?.captcha;
      if (!captcha?.protect) return resolve();
      let settled = false;
      const done = () => { if (!settled) { settled = true; resolve(); } };
      setTimeout(done, 4000);
      try { captcha.protect(form, done); } catch { done(); }
    });

  /**
   * Hand a customer form to the browser for a native submit, the path that
   * carries Shopify's CAPTCHA token and survives the /challenge round-trip.
   *
   * Shopify builds the challenge's return_path from the page URL, hash
   * included. A page opened via "#modal" would come back as "…%23modal" —
   * a literal path segment — and 404. So the hash is dropped first; the
   * form's own action already names the form (/contact#<form id>), which is
   * what Shopify uses to scroll back on return.
   */
  const submitNatively = (form) => {
    if (location.hash) history.replaceState(null, '', location.pathname + location.search);
    form.submit();
  };

  /* ── signup memory ─────────────────────────────────────────────────────── */

  const memory = new Map(); // fallback when localStorage throws (private mode, blocked)
  const store = {
    get() {
      try { return JSON.parse(localStorage.getItem(SIGNUPS_KEY)) || {}; } catch { return memory.get(SIGNUPS_KEY) || {}; }
    },
    set(value) {
      memory.set(SIGNUPS_KEY, value);
      try { localStorage.setItem(SIGNUPS_KEY, JSON.stringify(value)); } catch { /* ignore */ }
    },
  };

  window.SignupClient = {
    REVISION,
    outcomeFromUrl,
    submitNatively,

    /**
     * POST a {% form 'customer' %} without leaving the page.
     * Resolves 'new' | 'existing' | 'challenge'. On 'challenge' the caller
     * hands off with submitNatively(form); Shopify brings the page back with
     * the outcome in the URL and the form object, which the page renders.
     */
    async postCustomerForm(form) {
      await protect(form);
      const res = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!res.ok) throw new Error(`Shopify responded ${res.status}`);
      if (new URL(res.url).pathname === '/challenge') return 'challenge';
      return outcomeFromUrl(res.url) || 'new';
    },

    /**
     * What this browser has signed up for. A convenience for confirmations
     * ("you're on the list"), never a source of truth: it's per browser, and
     * Shopify/Klaviyo hold the real record. Keys are free-form, e.g.
     * 'newsletter' or 'waitlist:<variantId>'.
     */
    signups: {
      has(key) { return Boolean(store.get()[key]); },
      get(key) { return store.get()[key] || null; },
      record(key, data = {}) {
        const all = store.get();
        all[key] = { at: new Date().toISOString(), ...data };
        store.set(all);
      },
      forget(key) {
        const all = store.get();
        delete all[key];
        store.set(all);
      },
    },

    /** Marketing consent + list membership. */
    subscribeToList({ companyId, listId, email, source }) {
      return post('subscriptions', companyId, {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: source,
            profile: profile(email, {
              subscriptions: {
                email: { marketing: { consent: 'SUBSCRIBED', consented_at: new Date().toISOString() } },
              },
            }),
          },
          relationships: { list: { data: { type: 'list', id: listId } } },
        },
      });
    },

    /**
     * Custom properties. Its own request: Klaviyo won't subscribe and set
     * properties in one.
     *   properties — set/overwrite scalar values   ({ scent_preference: 'Floral' })
     *   append     — add to list-valued properties ({ waitlist_products: 'Dark Flower' });
     *                the list is created on first append, so no overwrite between products.
     *
     * Repeats are safe. Everything here keys on the email, so a second run
     * for the same address updates the one profile rather than making another;
     * `properties` overwrites; and `append` is made set-like on this side
     * (unappend then append the same value), since whether Klaviyo itself
     * dedupes a repeated append isn't documented.
     */
    setProfileProperties({ companyId, email, properties, append }) {
      const body = profile(email, properties ? { properties } : {});
      if (append) body.data.meta = { patch_properties: { unappend: append, append } };
      return post('profiles/', companyId, body, { contentType: 'application/json' });
    },

    /** A custom metric: the append-only way to record "did X with Y". */
    trackEvent({ companyId, email, metric, properties = {} }) {
      return post('events/', companyId, {
        data: {
          type: 'event',
          attributes: {
            properties,
            metric: { data: { type: 'metric', attributes: { name: metric } } },
            profile: profile(email),
          },
        },
      });
    },

    /**
     * Klaviyo's inventory-triggered back-in-stock, per variant. Not used while
     * the waitlist goes through the list, kept so switching back is one call.
     */
    subscribeBackInStock({ companyId, email, variantId }) {
      return post(
        'back-in-stock-subscriptions/',
        companyId,
        {
          data: {
            type: 'back-in-stock-subscription',
            attributes: { channels: ['EMAIL'], profile: profile(email) },
            relationships: {
              variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
            },
          },
        },
        { okStatuses: [409] },
      );
    },
  };
})();
